import {
  Image,
  Box,
  Text,
  List,
  Badge,
  Button,
  Flex,
  ActionIcon,
  Card,
  Group,
  useMantineTheme,
} from '@mantine/core';
import { trackAction, Actions } from 'utils/amplitude';
import { ActivitySearchResult } from 'types';
import { ActivityTypeEnum } from 'enums';
import classes from './ActivityCard.module.scss';
import { CloudinaryImage } from 'components/ui';
import {
  SingleSession,
  ComboBooking,
  BlockBooking,
  ChildAndGuardian,
  SubscriptionsFill,
} from '@icons';
import CategoryBadge from 'components/ui/CategoryBadge/CategoryBadge';
import { getActivityType } from 'utils/getActivityType';
import Link from 'next/link';
import BookNowButton from 'components/BookNowButton/BookNowButton';
import DaysOfWeekDisplayLine from 'components/ui/DaysOfWeekDisplayLine/DaysOfWeekDisplayLine';
import { getCalendarText, getTimeText } from 'pages/activities/[category]/[location]/utils';
import { useMediaQuery } from '@mantine/hooks';
import { useContext } from 'react';
import { EmbedContext, EmbedContextType } from 'context/EmbedContext';
import classNames from 'classnames';
import { CalendarBlank, CaretRight, Clock, MapPin, VideoCamera } from '@phosphor-icons/react';

interface IActivityCardProps {
  activity: ActivitySearchResult;
}

const ActivityCardEmbed: React.FC<IActivityCardProps> = (props) => {
  const theme = useMantineTheme();
  const { activity } = props;

  const { iframeLocation } = useContext(EmbedContext) as EmbedContextType;
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, true);

  const isSoldOut = !activity.anySpotsLeft;

  const isNotBookable = (): boolean => {
    return activity.isInPast || !activity.anySpotsLeft;
  };

  const getIconByActivityType = (type: ActivityTypeEnum) => {
    switch (type) {
      case ActivityTypeEnum.ONE_OFF_AND_BLOCK:
        return <ComboBooking size={18} color={theme.colors.gray[5]} />;
      case ActivityTypeEnum.BLOCK:
        return <BlockBooking size={18} color={theme.colors.gray[5]} />;
      case ActivityTypeEnum.SUBSCRIPTION:
        return <SubscriptionsFill size={24} title="Monthly subscription" />;
      default:
        return <SingleSession size={20} color={theme.colors.gray[5]} />;
    }
  };

  // For all trackAction on ActivityCard supplier and activity IDs are passed down manually
  // with following partially capitalized keys: supplierID, activityID
  // (on following pages are sent from sessionStorage which is nulled on activities search results page)
  const handleCardTrackEvent = () => {
    return trackAction(Actions.CARD_IFRAME, {
      supplierId: activity.supplierId,
      activityId: activity.id,
      activityLocation: activity.isOnline ? 'online' : 'in-person',
      activityType: activity.bookingType,
      iframeLocation,
    });
  };

  const handleBookNowTrackEvent = () => {
    return trackAction(Actions.INSTANT_IFRAME, {
      supplierId: activity.supplierId,
      activityId: activity.id,
      activityLocation: activity.isOnline ? 'online' : 'in-person',
      activityType: activity.bookingType,
      iframeLocation,
    });
  };

  const handleMoreDetailsTrackEvent = () => {
    return trackAction(Actions.MORE_DETAILS_IFRAME, {
      supplierId: activity.supplierId,
      activityId: activity.id,
      activityLocation: activity.isOnline ? 'online' : 'in-person',
      activityType: activity.bookingType,
      iframeLocation,
    });
  };

  return (
    <Card
      p={0}
      className={classNames(`${classes.card} activityCard`, {
        [classes.defaultCard]: !activity?.cloudinaryImageId,
      })}
      onClick={() => handleCardTrackEvent()}
      title={activity.name}
      miw={isMobile ? 'auto' : 295}
    >
      <Link href={`/activity/${activity.slug}`} passHref legacyBehavior>
        <section className={classes.cardHeader} aria-label="activity header">
          {activity?.cloudinaryImageId ? (
            <CloudinaryImage
              id={activity.cloudinaryImageId}
              alt={`${activity.name} - ${activity.supplierName}`}
              width={332}
              height={186}
            />
          ) : (
            <Image
              src="/pebble_placeholder_image.png"
              width="100%"
              height={186}
              fit="cover"
              alt="Pattern with shapes and lines"
              className={classes.image}
            />
          )}
          <div className={classes.categoryBadge}>
            {activity.categories &&
              activity.categories.map((category) => {
                return <CategoryBadge category={category} key={category.id}></CategoryBadge>;
              })}
          </div>
          <Badge className={classes.ageRange} leftSection={<ChildAndGuardian />}>
            {activity.ageRange}
          </Badge>
        </section>
      </Link>

      <Box className={classes.cardBody}>
        <Box className={classes.cardTitles}>
          <Group wrap={'nowrap'} justify="apart" align="start">
            <Link href={`/activity/${activity.slug}`} passHref legacyBehavior>
              <Text ta="left" size="md" lineClamp={2} fw={700} component="h2">
                {activity.name}
              </Text>
            </Link>
          </Group>

          {activity.isOnline ? (
            <Flex direction="row" align="center" my="xs" data-cy="activity-location">
              <Flex className={classes.onlineBadge} direction="row" align="center">
                <ActionIcon title="online video icon" variant="transparent">
                  <VideoCamera
                    size={18}
                    color={theme.colors.blue[8]}
                    weight="fill"
                    className={classes.onlineIcon}
                  />
                </ActionIcon>

                <Text>ONLINE</Text>
              </Flex>
              <Text className={classes.supplierName}>{activity.supplierName}</Text>
            </Flex>
          ) : (
            <Flex
              direction="row"
              align="center"
              data-cy="activity-location"
              className={classNames(classes.activityLocation)}
            >
              <ActionIcon
                title="location pin icon"
                className={classes.locationIcon}
                variant="subtle"
              >
                <MapPin size={18} weight="fill" color={theme.colors.blue[8]} />
              </ActionIcon>
              <Text ml="xs">{activity.location}</Text>
            </Flex>
          )}

          {activity.weekdays && (
            <Box pb="md">
              <DaysOfWeekDisplayLine weekdays={activity.weekdays} />
            </Box>
          )}
        </Box>

        <Flex justify={'space-between'} direction={'column'}>
          <Flex direction={'column'} mr={10}>
            <List listStyleType="none" className={classes.details}>
              <List.Item data-cy="activity-date">
                <Box className={classes.detailsItem}>
                  <CalendarBlank size={20} weight="fill" />
                  <Text fw="600" component="p">
                    {getCalendarText(activity.dateRange, activity.weekdays, activity.activityType)}
                  </Text>
                </Box>
              </List.Item>

              <List.Item data-cy="activity-time">
                <Box className={classNames(classes.detailsItem, classes.detailsMiddle)}>
                  <Clock size={20} weight="fill" />
                  <Text fw="600" component="p">
                    {getTimeText(activity.times)}
                  </Text>
                </Box>
              </List.Item>
              <List.Item>
                <Box className={classNames(classes.detailsItem, classes.detailsCenter)}>
                  <>
                    {getIconByActivityType(activity.activityType)}

                    <Text fw="600" component="p">
                      {getActivityType(activity.activityType)}
                    </Text>
                  </>
                </Box>
              </List.Item>
            </List>
          </Flex>
          <Flex>
            <Box component="section" className={classes.priceSection} mt={8}>
              {/* NOTE: to support instant booking*/}
              <Box hidden={isNotBookable()} w={'100%'}>
                <Link
                  href={`/activity/${activity.slug}#times-and-availability`}
                  passHref
                  legacyBehavior
                >
                  <BookNowButton
                    radius={8}
                    component="a"
                    onClick={() => handleBookNowTrackEvent()}
                    rightSection={
                      <CaretRight color={theme.colors.blue[8]} weight="bold" size={14} />
                    }
                    w={'100%'}
                  >
                    {activity.callToAction}
                  </BookNowButton>
                </Link>
              </Box>
              {isSoldOut && (
                <Button
                  href={`/activity/${activity.slug}#times-and-availability`}
                  component="a"
                  className={classes.enquire}
                  onClick={() => trackAction(Actions.WAITLIST_CTA)}
                  w={'100%'}
                >
                  Waitlist
                </Button>
              )}
              <Link href={`/activity/${activity.slug}`} passHref legacyBehavior>
                <Button
                  className={classes.moreDetails}
                  component="a"
                  onClick={() => handleMoreDetailsTrackEvent()}
                  variant="outline"
                  w={'100%'}
                >
                  More details
                </Button>
              </Link>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Card>
  );
};

export default ActivityCardEmbed;
